import React, { useState, useEffect } from 'react';
import { getAllFonctions, createFonction, updateFonction, deleteFonction, getFonctionById } from '../services/api';

const Fonctions = () => {
  const [fonctions, setFonctions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingFonction, setEditingFonction] = useState(null);
  const [selectedFonction, setSelectedFonction] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [formData, setFormData] = useState({
    nom: '',
    code: '',
    description: '',
    statut: 'actif',
  });

  useEffect(() => {
    loadFonctions();
  }, []);

  const loadFonctions = async () => {
    setLoading(true);
    try {
      const data = await getAllFonctions();
      setFonctions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur lors du chargement des fonctions:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des fonctions' });
      setFonctions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateFonction = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.nom) {
      setMessage({ type: 'error', text: 'Le nom est requis' });
      return;
    }

    setLoading(true);
    try {
      await createFonction(formData);
      setMessage({ type: 'success', text: 'Fonction créée avec succès' });
      setShowCreateForm(false);
      resetForm();
      loadFonctions();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la création de la fonction',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFonction = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    setLoading(true);
    try {
      await updateFonction(editingFonction.id, formData);
      setMessage({ type: 'success', text: 'Fonction mise à jour avec succès' });
      setEditingFonction(null);
      resetForm();
      loadFonctions();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la mise à jour de la fonction',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFonction = async (fonctionId, nom) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la fonction "${nom}" ?`)) {
      return;
    }

    setLoading(true);
    try {
      await deleteFonction(fonctionId);
      setMessage({ type: 'success', text: 'Fonction supprimée avec succès' });
      loadFonctions();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la suppression de la fonction',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditFonction = (fonction) => {
    setEditingFonction(fonction);
    setFormData({
      nom: fonction.nom || '',
      code: fonction.code || '',
      description: fonction.description || '',
      statut: fonction.statut || 'actif',
    });
    setShowCreateForm(true);
  };

  const handleViewDetails = async (id) => {
    setLoadingDetails(true);
    setShowDetails(true);
    try {
      const fonction = await getFonctionById(id);
      setSelectedFonction(fonction);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des détails' });
    } finally {
      setLoadingDetails(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      code: '',
      description: '',
      statut: 'actif',
    });
    setEditingFonction(null);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    resetForm();
    setMessage({ type: '', text: '' });
  };

  // Pagination
  const totalPages = Math.ceil(fonctions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentFonctions = fonctions.slice(startIndex, endIndex);

  return (
    <div className="p-8 bg-background min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-text flex items-center">
            <i className="fas fa-briefcase mr-3 text-primary"></i>
            Gestion des Fonctions
          </h1>
          <button
            onClick={() => {
              resetForm();
              setShowCreateForm(true);
            }}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all"
          >
            <i className="fas fa-plus mr-2"></i> Nouvelle Fonction
          </button>
        </div>

        {message.text && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {message.text}
          </div>
        )}

        {showCreateForm && (
          <div className="bg-cardBg rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-text mb-4">
              {editingFonction ? 'Modifier la Fonction' : 'Nouvelle Fonction'}
            </h2>
            <form onSubmit={editingFonction ? handleUpdateFonction : handleCreateFonction}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Nom *</label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Code</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-text mb-2">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Statut</label>
                  <select
                    name="statut"
                    value={formData.statut}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="actif">Actif</option>
                    <option value="inactif">Inactif</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 text-text rounded-lg hover:bg-gray-100 transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all disabled:opacity-50"
                >
                  {loading ? 'Enregistrement...' : editingFonction ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading && !showCreateForm ? (
          <div className="text-center py-8">
            <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
            <p className="text-text-light">Chargement des fonctions...</p>
          </div>
        ) : (
          <>
            <div className="bg-cardBg rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentFonctions.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-text-light">
                          Aucune fonction trouvée
                        </td>
                      </tr>
                    ) : (
                      currentFonctions.map((fonction) => (
                        <tr key={fonction.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-text">{fonction.id}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text">{fonction.nom}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-text">{fonction.code || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                fonction.statut === 'actif'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {fonction.statut}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <button
                              onClick={() => handleViewDetails(fonction.id)}
                              className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all text-sm"
                              title="Voir les détails"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              onClick={() => handleEditFonction(fonction)}
                              className="px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-all text-sm"
                              title="Modifier"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteFonction(fonction.id, fonction.nom)}
                              className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all text-sm"
                              title="Supprimer"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex justify-center items-center gap-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <span className="text-text">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}

        {/* Modal Détails */}
        {showDetails && selectedFonction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-text">Détails de la Fonction</h2>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedFonction(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
              {loadingDetails ? (
                <div className="text-center py-8">
                  <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
                  <p className="text-text-light">Chargement...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-text-light">ID</label>
                    <p className="text-text">{selectedFonction.id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-light">Nom</label>
                    <p className="text-text">{selectedFonction.nom}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-light">Code</label>
                    <p className="text-text">{selectedFonction.code || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-light">Description</label>
                    <p className="text-text">{selectedFonction.description || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-light">Statut</label>
                    <p className="text-text">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          selectedFonction.statut === 'actif'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {selectedFonction.statut}
                      </span>
                    </p>
                  </div>
                  {selectedFonction.date_creation && (
                    <div>
                      <label className="text-sm font-medium text-text-light">Date de Création</label>
                      <p className="text-text">{new Date(selectedFonction.date_creation).toLocaleString('fr-FR')}</p>
                    </div>
                  )}
                  {selectedFonction.date_modification && (
                    <div>
                      <label className="text-sm font-medium text-text-light">Date de Modification</label>
                      <p className="text-text">{new Date(selectedFonction.date_modification).toLocaleString('fr-FR')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Fonctions;

